import { fetchQueries } from './Query';
import { fetchBookings } from './Booking';
import { fetchFollowupsForToday } from './Followup';
import { fetchCustomers } from './Customer';
import { getApiHeaders } from '../utils/apiHeaders';
import { fetchTeamMembers } from './User';
import { jwtDecode } from 'jwt-decode';
import { UserRole } from '../types/Role';
import { fetchTelecallingSummary } from './Telecalling';

export type DashboardData = {
  queries: any[];
  recentQueries: any[];
  queriesOverTime: Array<{ date: string; count: number }>;
  queriesByType?: Array<{ type: string; count: number }>;
  queriesByAssignee?: Array<{ assigneeId: number | string; assigneeName: string; count: number }>;
  followups: any[];
  bookings: any[];
  paymentsReceived: Array<{ date: string; customer: string; amount: number; customerId?: number | string }>;
  paymentsPending: Array<{ date: string; customer: string; amount: number; customerId?: number | string }>;
  bookingsByDate: Array<{ date: string; type: string; amount: number }>;
  departures: Array<{ title: string; date: string; customer: string; customerId?: number | string }>;
  stats: {
    customersTotal: number;
    customersNew30: number;
    queriesTotal: number;
    queriesNew30: number;
    followupsToday: number;
    bookedAmt30: number;
    paidAmt30: number;
    markupAmt30: number;
    telecallingAssignedToday: number;
    telecallingCompletedToday: number;
    telecallingConvertedToday: number;
  };
};

function ymdLocal(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const getReceivableAmount = (booking: any) => {
  const paise = booking?.total_amount_receivable_paise
    ?? booking?.totalAmountReceivablePaise
    ?? booking?.total_amount_receivable;
  if (paise !== undefined && paise !== null && Number.isFinite(Number(paise))) return Number(paise) / 100;
  return Number(booking?.total_price || 0);
};

export const isCancelledBooking = (booking: any) => String(booking?.status || '').trim().toUpperCase() === 'CANCELLED';

const getMarkupAmount = (booking: any) => {
  const paise = booking?.markup_paise ?? booking?.markupPaise ?? booking?.markup;
  return paise !== undefined && paise !== null && Number.isFinite(Number(paise)) ? Number(paise) / 100 : 0;
};

export const resolveMarkupAmount30 = (
  stats: { markupAmt30?: unknown } | undefined,
  rawMarkupAmount: unknown,
  bookings: any[],
) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  const bookingsWithMarkup = bookings.filter((booking) => {
    const paise = booking?.markup_paise ?? booking?.markupPaise ?? booking?.markup;
    const createdAt = booking?.created_at || booking?.createdAt;
    const isRecent = !createdAt || new Date(createdAt) >= cutoff;
    return isRecent && paise !== undefined && paise !== null && Number.isFinite(Number(paise));
  });

  if (bookingsWithMarkup.length > 0) {
    return bookingsWithMarkup.reduce((sum: number, booking: any) => sum + getMarkupAmount(booking), 0);
  }

  const aggregatedMarkup = stats?.markupAmt30 ?? rawMarkupAmount;
  return Number.isFinite(Number(aggregatedMarkup)) ? Number(aggregatedMarkup) : 0;
};

/**
 * Build travel-specific dashboard cards from bookings that have already passed
 * the current user's visibility filter. The travel dashboard endpoint returns
 * global payment and departure aggregates, so scoped users must not consume
 * those aggregates directly.
 */
export function buildScopedTravelDashboardViews(
  bookings: any[],
  now = new Date(),
  dateRange?: { from?: string; to?: string },
) {
  const d30 = new Date(now);
  d30.setDate(d30.getDate() - 30);
  const d7 = new Date(now);
  d7.setDate(d7.getDate() - 6);
  d7.setHours(0, 0, 0, 0);
  const rangeStart = dateRange?.from ? new Date(`${dateRange.from}T00:00:00`) : d30;
  const rangeEnd = dateRange?.to ? new Date(`${dateRange.to}T23:59:59.999`) : now;

  const recentBookings = bookings.filter((booking) => {
    const createdAt = booking.created_at ? new Date(booking.created_at) : null;
    return createdAt && createdAt >= rangeStart && createdAt <= rangeEnd && !isCancelledBooking(booking);
  });
  const bookedAmt30 = recentBookings.reduce((sum, booking) => sum + getReceivableAmount(booking), 0);
  const paidAmt30 = recentBookings.reduce((sum, booking) => {
    const status = String(booking.payment_status || booking.status || '').toUpperCase();
    return sum + (status.includes('CAPTURE') || status.includes('PAID') || status.includes('SUCCESS') ? getReceivableAmount(booking) : 0);
  }, 0);
  const markupAmt30 = recentBookings.reduce((sum, booking) => sum + getMarkupAmount(booking), 0);

  const paymentsReceived: DashboardData['paymentsReceived'] = [];
  const paymentsPending: DashboardData['paymentsPending'] = [];
  bookings.forEach((booking) => {
    const amount = getReceivableAmount(booking);
    const payment = {
      date: booking.updated_at || booking.created_at || '',
      customer: booking.customer?.name || '—',
      amount,
      customerId: booking.customer?.id ?? booking.customerId ?? booking.customer_id,
    };
    const status = String(booking.payment_status || booking.status || '').toUpperCase();
    (status.includes('CAPTURE') || status.includes('PAID') || status.includes('SUCCESS') ? paymentsReceived : paymentsPending).push(payment);
  });
  const byNewest = (a: { date: string }, b: { date: string }) => Date.parse(b.date) - Date.parse(a.date);
  paymentsReceived.sort(byNewest);
  paymentsPending.sort(byNewest);

  const dateKeys = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(d7);
    date.setDate(d7.getDate() + index);
    return ymdLocal(date);
  });
  const totalsByDate: Record<string, Record<string, number>> = {};
  dateKeys.forEach((date) => { totalsByDate[date] = {}; });
  bookings.forEach((booking) => {
    const date = booking.created_at || booking.updated_at || booking.travel_start_date;
    const key = date ? ymdLocal(new Date(date)) : '';
    if (!totalsByDate[key]) return;
    const type = String(booking.product_type || '').toLowerCase() || 'other';
    totalsByDate[key][type] = (totalsByDate[key][type] || 0) + getReceivableAmount(booking);
  });
  const bookingsByDate = dateKeys.flatMap((date) =>
    Object.entries(totalsByDate[date]).map(([type, amount]) => ({ date, type, amount }))
  );

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const departureEnd = new Date(today);
  departureEnd.setDate(departureEnd.getDate() + 7);
  const departures = bookings
    .filter((booking) => {
      const date = booking.travel_start_date ? new Date(booking.travel_start_date) : null;
      return date && date >= today && date <= departureEnd;
    })
    .slice(0, 6)
    .map((booking) => ({
      title: booking.package?.title || '—',
      date: new Date(booking.travel_start_date).toISOString(),
      customer: booking.customer?.name || '—',
      customerId: booking.customer?.id ?? booking.customerId ?? booking.customer_id,
    }));

  return {
    paymentsReceived: paymentsReceived.slice(0, 7),
    paymentsPending: paymentsPending.slice(0, 7),
    bookingsByDate,
    departures,
    stats: { bookedAmt30, paidAmt30, markupAmt30 },
  };
}

export type DashboardFilters = {
  queryType?: string;
  assignee?: string;
  month?: '' | 'prev' | 'last3' | 'ytd';
  from?: string; // YYYY-MM-DD
  to?: string;   // YYYY-MM-DD
  tz?: string;
  byDateDays?: number;
  paymentsLimit?: number;
};

function monthToRange(month?: '' | 'prev' | 'last3' | 'ytd', tz?: string): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const to = ymdLocal(now);
  const firstOfMonth = new Date(y, m, 1);
  if (!month) return { from: ymdLocal(firstOfMonth), to };
  if (month === 'prev') {
    const prevFirst = new Date(y, m - 1, 1);
    const prevLast = new Date(y, m, 0);
    return { from: ymdLocal(prevFirst), to: ymdLocal(prevLast) };
    }
  if (month === 'last3') {
    const from = new Date(y, m - 2, 1);
    return { from: ymdLocal(from), to };
  }
  if (month === 'ytd') {
    const from = new Date(y, 0, 1);
    return { from: ymdLocal(from), to };
  }
  return { from: ymdLocal(firstOfMonth), to };
}

function withParams(base: string, params: Record<string, any>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    usp.set(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Get current user info from JWT token
 */
function getCurrentUser(): { id?: number; role?: UserRole } | null {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const decoded = jwtDecode(token) as any;
    return {
      id: decoded.id || decoded.user_id,
      role: decoded.role as UserRole,
    };
  } catch {
    return null;
  }
}

/**
 * Get user IDs to filter by based on role
 * Returns null for admin (no filtering), or array of user IDs for others
 */
async function getUserIdsForFiltering(user: { id?: number; role?: UserRole } | null): Promise<number[] | null> {
  if (!user || !user.id) return null;
  
  const role = user.role;
  
  // Admin sees all - no filtering
  if (role === 'admin') {
    return null;
  }
  
  // Sales/Ops see only their own data
  if (role === 'sales' || role === 'ops') {
    return [user.id];
  }
  
  // Manager sees their own + team members' data
  if (role === 'sales-manager') {
    try {
      const teamMembers = await fetchTeamMembers(user.id);
      const teamIds = teamMembers.map(m => m.id).filter((id): id is number => id !== undefined);
      return [user.id, ...teamIds];
    } catch {
      // If team fetch fails, just return own ID
      return [user.id];
    }
  }
  
  // Default: only own data
  return [user.id];
}

/**
 * Filter data array by user IDs (assigned_to or created_by)
 * For followups, also checks related query's assigned_to/created_by
 */
function filterByUserIds<T extends { assigned_to?: number; assignedTo?: { id?: number }; created_by?: number; queryId?: number; query?: { assigned_to?: number; assignedTo?: { id?: number }; created_by?: number } }>(
  data: T[],
  userIds: number[] | null,
  relatedQueries?: Array<{ id?: number; assigned_to?: number; assignedTo?: { id?: number }; created_by?: number }>
): T[] {
  if (!userIds) return data; // Admin sees all
  
  return data.filter((item) => {
    const assignedToId = item.assigned_to || item.assignedTo?.id;
    const createdById = item.created_by;
    
    // Check direct assignment/creation
    if ((assignedToId !== undefined && userIds.includes(assignedToId)) ||
        (createdById !== undefined && userIds.includes(createdById))) {
      return true;
    }
    
    // For followups, check related query
    if (item.queryId !== undefined && relatedQueries) {
      const relatedQuery = relatedQueries.find(q => q.id === item.queryId);
      if (relatedQuery) {
        const queryAssignedToId = relatedQuery.assigned_to || relatedQuery.assignedTo?.id;
        const queryCreatedById = relatedQuery.created_by;
        if ((queryAssignedToId !== undefined && userIds.includes(queryAssignedToId)) ||
            (queryCreatedById !== undefined && userIds.includes(queryCreatedById))) {
          return true;
        }
      }
    }
    
    // Also check nested query object
    if (item.query) {
      const queryAssignedToId = item.query.assigned_to || item.query.assignedTo?.id;
      const queryCreatedById = item.query.created_by;
      if ((queryAssignedToId !== undefined && userIds.includes(queryAssignedToId)) ||
          (queryCreatedById !== undefined && userIds.includes(queryCreatedById))) {
        return true;
      }
    }
    
    return false;
  });
}

export async function fetchDashboardData(filters?: DashboardFilters): Promise<DashboardData> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const tz = filters?.tz || 'UTC';
  const range = filters?.from && filters?.to ? { from: filters.from, to: filters.to } : monthToRange(filters?.month, tz);
  const byDateDays = filters?.byDateDays ?? 7;
  const paymentsLimit = filters?.paymentsLimit ?? 7;

  // Get current user and determine filtering
  const currentUser = getCurrentUser();
  const userIdsForFiltering = await getUserIdsForFiltering(currentUser);
  const today = ymdLocal(new Date());

  const telecallingTotals = await (async () => {
    try {
      const summary = await fetchTelecallingSummary({ date: today });
      if (userIdsForFiltering && Array.isArray(summary.byUser)) {
        const totals = summary.byUser
          .filter((row) => userIdsForFiltering.includes(row.userId))
          .reduce(
            (acc, row) => {
              acc.assigned += row.assigned || 0;
              acc.completed += row.completed || 0;
              acc.converted += row.converted || 0;
              return acc;
            },
            { assigned: 0, completed: 0, converted: 0 }
          );
        return totals;
      }
      return {
        assigned: summary.totals?.assigned || 0,
        completed: summary.totals?.completed || 0,
        converted: summary.totals?.converted || 0,
      };
    } catch {
      return { assigned: 0, completed: 0, converted: 0 };
    }
  })();

  // 1) Try separate dashboard endpoints and merge
  try {
    let qRaw: any = null;
    let tRaw: any = null;
    // Query backend dashboard
    // Note: Backend should handle role-based filtering, but we pass user context
    if (API_BASE_URL) {
      const qUrl = withParams(`${API_BASE_URL}/api/dashboard`, {
        tz,
        from: range.from,
        to: range.to,
        assignee: filters?.assignee,
        // Backend should extract user from JWT, but we can pass explicit filter if needed
      });
      const r = await fetch(qUrl, {
        headers: getApiHeaders('none'),
      });
      if (r.ok) qRaw = await r.json();
    }
    // Package orders and their payment ledger are CRM-owned. This must never
    // fall back to the supplier-integration service.
    if (API_BASE_URL) {
      try {
        const url = withParams(`${API_BASE_URL}/api/dashboard/package-orders`, { tz, from: range.from, to: range.to, byDateDays, paymentsLimit });
        const r = await fetch(url, { headers: getApiHeaders('none') });
        if (r.ok) tRaw = await r.json();
      } catch {}
    }

    if (qRaw || tRaw) {
      // Normalize Queries/Customers/Followups side
      let qQueries = (qRaw?.queries || qRaw?.recent_queries || []) as any[];
      let qFollowups = (qRaw?.followups || qRaw?.today_followups || []) as any[];
      
      // Apply role-based filtering if backend didn't handle it
      if (userIdsForFiltering !== null) {
        qQueries = filterByUserIds(qQueries, userIdsForFiltering);
        // Filter followups by their related queries
        qFollowups = filterByUserIds(qFollowups, userIdsForFiltering, qQueries);
      }
      const qStats = qRaw?.stats || {};
      let queriesOverTime = qRaw?.queriesOverTime || qRaw?.queries_over_time || [];
      if ((!queriesOverTime || queriesOverTime.length === 0) && Array.isArray(qQueries)) {
        const now = new Date(); const start = new Date(now); start.setDate(now.getDate() - 29);
        const buckets: Record<string, number> = {}; for (let i=0;i<30;i++){ const d = new Date(start); d.setDate(start.getDate()+i); buckets[ymdLocal(d)] = 0; }
        qQueries.forEach(q => { const ds = q.queryDate || q.query_date || q.created_at || q.createdAt; if (!ds) return; const k = ymdLocal(new Date(ds)); if (k in buckets) buckets[k] += 1; });
        queriesOverTime = Object.keys(buckets).map(k => ({ date: k, count: buckets[k] }));
      }
      const recentQueries = (qRaw?.recentQueries || qRaw?.recent_queries) ?? (Array.isArray(qQueries) ? qQueries.slice().sort((a:any,b:any)=> new Date(b.queryDate||b.created_at).getTime() - new Date(a.queryDate||a.created_at).getTime()).slice(0,7) : []);

      // By-type and by-assignee summaries if provided
      const queriesByType = qRaw?.queriesByType || qRaw?.queries_by_type || undefined;
      const queriesByAssignee = qRaw?.queriesByAssignee || qRaw?.queries_by_assignee || undefined;
      const customersTotal = qStats.customersTotal ?? qRaw?.customers_total ?? 0;
      const customersNew30 = qStats.customersNew30 ?? qRaw?.customers_new_30 ?? 0;
      const queriesTotal = qStats.queriesTotal ?? qRaw?.queries_total ?? (Array.isArray(qQueries) ? qQueries.length : 0);
      const queriesNew30 = qStats.queriesNew30 ?? qRaw?.queries_new_30 ?? 0;
      const followupsToday = qStats.followupsToday ?? qRaw?.followups_today ?? (Array.isArray(qFollowups) ? qFollowups.length : 0);

      // Normalize Travel/Payments/Bookings side
      let tBookings = (tRaw?.bookings || []) as any[];
      
      // Apply role-based filtering if backend didn't handle it
      if (userIdsForFiltering !== null) {
        // The dashboard endpoint returns global aggregates. Fetch the booking
        // records for scoped users so every travel card can be rebuilt from a
        // single, visibility-safe source.
        const directBookings = await fetchBookings().catch(() => []);
        tBookings = filterByUserIds(
          Array.isArray(directBookings) ? directBookings : tBookings,
          userIdsForFiltering,
        );
      }
      // Use booking records, rather than the travel dashboard's fixed 30-day
      // aggregate, so markup always follows the selected date range.
      const directMarkupBookings = await fetchBookings().catch(() => []);
      let markupBookings = Array.isArray(directMarkupBookings) && directMarkupBookings.length > 0
        ? directMarkupBookings
        : tBookings;
      if (userIdsForFiltering !== null) {
        markupBookings = filterByUserIds(markupBookings, userIdsForFiltering);
      }
      const scopedTravelViews = userIdsForFiltering !== null
        ? buildScopedTravelDashboardViews(tBookings, new Date(), range)
        : null;
      const dateRangeTravelViews = buildScopedTravelDashboardViews(markupBookings, new Date(), range);
      const paymentsReceived = scopedTravelViews?.paymentsReceived
        ?? (tRaw?.paymentsReceived || tRaw?.payments_received || []);
      const paymentsPending = scopedTravelViews?.paymentsPending
        ?? (tRaw?.paymentsPending || tRaw?.payments_pending || []);
      let bookingsByDate = tRaw?.bookingsByDate || tRaw?.bookings_by_date || [];
      if (scopedTravelViews) bookingsByDate = scopedTravelViews.bookingsByDate;
      if ((!bookingsByDate || bookingsByDate.length === 0) && Array.isArray(tBookings)) {
        const now = new Date(); const d7 = new Date(now); d7.setDate(now.getDate() - 6); d7.setHours(0,0,0,0);
        const dateKeys: string[] = []; for (let i=0;i<7;i++){ const d=new Date(d7); d.setDate(d7.getDate()+i); dateKeys.push(ymdLocal(d)); }
        const sums: Record<string, Record<string, number>> = {}; dateKeys.forEach(k=> sums[k] = {});
        tBookings.forEach((b: any) => {
          const when = b.created_at || b.updated_at || b.travel_start_date || b.start_at;
          const k = when ? ymdLocal(new Date(when)) : '';
          if (!sums[k]) return;
          const t = String(b.product_type || '').toLowerCase() || 'other';
          sums[k][t] = (sums[k][t] || 0) + getReceivableAmount(b);
        });
        bookingsByDate = dateKeys.flatMap(k => Object.entries(sums[k]).map(([t,amt]) => ({ date:k, type:t, amount:amt })));
      }
      const departures = scopedTravelViews?.departures ?? (tRaw?.departures || []);
      const tStats = tRaw?.stats || {};
      const bookedAmt30 = scopedTravelViews?.stats.bookedAmt30 ?? (tBookings.length > 0
        ? tBookings
          .filter((booking: any) => !isCancelledBooking(booking))
          .reduce((sum: number, booking: any) => sum + getReceivableAmount(booking), 0)
        : tStats.bookedAmt30 ?? tRaw?.booked_amt_30 ?? 0);
      const paidAmt30 = scopedTravelViews?.stats.paidAmt30
        ?? tStats.paidAmt30
        ?? tRaw?.paid_amt_30
        ?? 0;
      const markupAmt30 = dateRangeTravelViews.stats.markupAmt30;

      return {
        queries: qQueries || [],
        recentQueries,
        queriesOverTime,
        queriesByType,
        queriesByAssignee,
        followups: qFollowups || [],
        bookings: tBookings || [],
        paymentsReceived,
        paymentsPending,
        bookingsByDate,
        departures,
        stats: {
          customersTotal,
          customersNew30,
          queriesTotal,
          queriesNew30,
          followupsToday,
          bookedAmt30,
          paidAmt30,
          markupAmt30,
          telecallingAssignedToday: telecallingTotals.assigned,
          telecallingCompletedToday: telecallingTotals.completed,
          telecallingConvertedToday: telecallingTotals.converted,
        },
      };
    }
  } catch {}

  // 2) Fallback: compose from existing raw APIs (single-shot)
  const [queriesRaw, bookingsRaw, followupsRaw, customersRaw] = await Promise.all([
    fetchQueries().catch(() => []),
    fetchBookings().catch(() => []),
    fetchFollowupsForToday().catch(() => []),
    fetchCustomers().catch(() => []),
  ]);

  // Apply filters to queries
  let queries = Array.isArray(queriesRaw) ? queriesRaw : [];
  
  // Apply role-based filtering first
  if (userIdsForFiltering !== null) {
    queries = filterByUserIds(queries, userIdsForFiltering);
  }
  
  // Filter by assignee (if specified, overrides role-based filter for that assignee)
  if (filters?.assignee) {
    queries = queries.filter((q: any) => {
      const assigneeId = q.assignedTo?.id != null ? String(q.assignedTo.id) : 'unassigned';
      return assigneeId === String(filters.assignee);
    });
  }
  
  // Filter by date range (reuse the range variable declared at the top)
  if (range.from && range.to) {
    const fromDate = new Date(range.from);
    const toDate = new Date(range.to);
    toDate.setHours(23, 59, 59, 999); // Include the entire end date
    queries = queries.filter((q: any) => {
      const qDate = q.queryDate ? new Date(q.queryDate) : (q.created_at ? new Date(q.created_at) : null);
      if (!qDate) return false;
      return qDate >= fromDate && qDate <= toDate;
    });
  }

  // Apply date range filter to bookings
  let bookings = Array.isArray(bookingsRaw) ? bookingsRaw : [];
  
  // Apply role-based filtering first
  if (userIdsForFiltering !== null) {
    bookings = filterByUserIds(bookings, userIdsForFiltering);
  }
  
  if (range.from && range.to) {
    const fromDate = new Date(range.from);
    const toDate = new Date(range.to);
    toDate.setHours(23, 59, 59, 999);
    bookings = bookings.filter((b: any) => {
      const bDate = b.created_at ? new Date(b.created_at) : (b.updated_at ? new Date(b.updated_at) : null);
      if (!bDate) return false;
      return bDate >= fromDate && bDate <= toDate;
    });
  }

  let followups = Array.isArray(followupsRaw) ? followupsRaw : [];
  let customers = Array.isArray(customersRaw) ? customersRaw : [];
  
  // Apply role-based filtering to followups (filter by related queries)
  if (userIdsForFiltering !== null) {
    followups = filterByUserIds(followups, userIdsForFiltering, queries);
  }
  
  // For customers, filter by created_by if available
  // If customers don't have created_by, we show all customers (they're shared entities)
  // but we can filter based on associated queries/bookings if needed
  if (userIdsForFiltering !== null) {
    const filteredCustomers = customers.filter((c: any) => {
      const createdById = c.created_by || c.createdBy?.id;
      // If customer has created_by, filter by it; otherwise include it
      return createdById === undefined || userIdsForFiltering.includes(createdById);
    });
    
    // Also include customers that have queries/bookings assigned to the user
    const customerIdsFromQueries = queries.map((q: any) => q.customerId || q.customer_id).filter(Boolean);
    const customerIdsFromBookings = bookings.map((b: any) => b.customer_id).filter(Boolean);
    const allRelevantCustomerIds = new Set([...customerIdsFromQueries, ...customerIdsFromBookings]);
    
    // Include customers that are referenced in filtered queries/bookings
    const relevantCustomerIdsArray = Array.from(allRelevantCustomerIds);
    customers = customers.filter((c: any) => {
      const customerId = c.id;
      return filteredCustomers.some((fc: any) => fc.id === customerId) ||
             relevantCustomerIdsArray.includes(customerId);
    });
  }

  const now = new Date();
  const d30 = new Date(now); d30.setDate(now.getDate() - 30);
  const d7 = new Date(now); d7.setDate(now.getDate() - 6); d7.setHours(0,0,0,0);

  const customersTotal = customers.length;
  const customersNew30 = customers.filter((c:any)=>{
    const dt = c.created_at ? new Date(c.created_at) : null; return dt && dt >= d30;
  }).length;

  // Queries
  const queriesTotal = queries.length;
  const queriesNew30 = queries.filter((q:any)=>{
    const dt = q.queryDate ? new Date(q.queryDate) : null; return dt && dt >= d30;
  }).length;
  const recentQueries = queries
    .slice()
    .sort((a:any,b:any)=> {
      const dateA = a.queryDate ? new Date(a.queryDate) : (a.created_at ? new Date(a.created_at) : new Date(0));
      const dateB = b.queryDate ? new Date(b.queryDate) : (b.created_at ? new Date(b.created_at) : new Date(0));
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0,7);
  // queries over last 30 days (or within the filtered range)
  const qoBuckets: Record<string, number> = {};
  const labelsArr: string[] = [];
  const startDate = range.from ? new Date(range.from) : (new Date(now)); startDate.setDate(now.getDate() - 29);
  const endDate = range.to ? new Date(range.to) : now;
  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const daysToShow = Math.min(Math.max(daysDiff, 1), 30);
  for (let i=0;i<daysToShow;i++){ 
    const d=new Date(startDate); 
    d.setDate(startDate.getDate()+i); 
    const k=ymdLocal(d); 
    qoBuckets[k]=0; 
    labelsArr.push(k); 
  }
  queries.forEach((q:any) => {
    const dq = q.queryDate ? new Date(q.queryDate) : (q.created_at ? new Date(q.created_at) : null);
    if (!dq) return;
    const k = ymdLocal(dq);
    if (k in qoBuckets) qoBuckets[k] += 1;
  });
  const queriesOverTime = labelsArr.map(k => ({ date: k, count: qoBuckets[k] || 0 }));

  // queries by type & assignee (fallback composition)
  const queriesByType = queries.reduce((acc: Record<string, number>, q: any) => {
    const t = q.type || 'Other';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const queriesByTypeArr = Object.keys(queriesByType).map(type => ({ type, count: queriesByType[type] }));
  const queriesByAssigneeMap = queries.reduce((acc: Record<string, { assigneeId: string; assigneeName: string; count: number }>, q: any) => {
    const id = q.assignedTo?.id != null ? String(q.assignedTo.id) : 'unassigned';
    const name = q.assignedTo?.name || 'Unassigned';
    if (!acc[id]) acc[id] = { assigneeId: id, assigneeName: name, count: 0 };
    acc[id].count += 1;
    return acc;
  }, {} as Record<string, { assigneeId: string; assigneeName: string; count: number }>);
  const queriesByAssigneeArr = Object.values(queriesByAssigneeMap);

  // Bookings and payments
  const bs = bookings;
  const in30 = bs.filter((b:any)=>{ const dt=b.created_at?new Date(b.created_at):null; return dt && dt>=d30 && !isCancelledBooking(b); });
  const bookedAmt30 = in30.reduce((sum:number,b:any)=> sum + getReceivableAmount(b), 0);
  const paidAmt30 = in30.reduce((sum:number,b:any)=>{ const st=String(b.payment_status||b.status||'').toUpperCase(); const paid = st.includes('CAPTURE')||st.includes('PAID')||st.includes('SUCCESS'); return sum + (paid ? getReceivableAmount(b) : 0); }, 0);
  const markupAmt30 = in30.reduce((sum: number, booking: any) => sum + getMarkupAmount(booking), 0);

  // Payments lists (received vs pending)
  const received: Array<{ date: string; customer: string; amount: number; customerId?: number | string }> = [];
  const pending: Array<{ date: string; customer: string; amount: number; customerId?: number | string }> = [];
  const fmt = (iso?: string) => { if (!iso) return '—'; const d = new Date(iso); return d.toISOString(); };
  bs.forEach((b:any)=>{
    const amt = getReceivableAmount(b);
    const name = b.customer?.name || '—';
    const date = fmt(b.updated_at || b.created_at);
    const st = String(b.payment_status || b.status || '').toUpperCase();
    const isRec = st.includes('CAPTURE')||st.includes('PAID')||st.includes('SUCCESS');
    (isRec ? received : pending).push({ date, customer: name, amount: amt, customerId: b.customer?.id ?? b.customerId ?? b.customer_id });
  });
  received.sort((a,b)=> Date.parse(b.date) - Date.parse(a.date)); pending.sort((a,b)=> Date.parse(b.date) - Date.parse(a.date));

  // Bookings by date and type (last 7 days or within filtered range)
  const dateKeys: string[] = [];
  const bookingsStartDate = range.from ? new Date(range.from) : d7;
  const bookingsEndDate = range.to ? new Date(range.to) : new Date(now);
  const bookingsDays = Math.ceil((bookingsEndDate.getTime() - bookingsStartDate.getTime()) / (1000 * 60 * 60 * 24));
  const daysToShowBookings = Math.min(Math.max(bookingsDays, 1), 7);
  for (let i=0;i<daysToShowBookings;i++){ 
    const d=new Date(bookingsStartDate); 
    d.setDate(bookingsStartDate.getDate()+i); 
    dateKeys.push(ymdLocal(d)); 
  }
  const bookingsByDate: Array<{ date: string; type: string; amount: number }> = [];
  const sums: Record<string, Record<string, number>> = {};
  dateKeys.forEach(k=> sums[k] = {});
  bs.forEach((b:any)=>{
    const k = ymdLocal(new Date(b.created_at || b.updated_at || b.travel_start_date || Date.now()));
    if (!sums[k]) return;
    const t = String(b.product_type || '').toLowerCase() || 'other';
    sums[k][t] = (sums[k][t] || 0) + getReceivableAmount(b);
  });
  dateKeys.forEach(k=> { Object.entries(sums[k]).forEach(([t,amt])=> bookingsByDate.push({ date:k, type:t, amount:amt })); });

  // Departures next 7 days
  const now0 = new Date(); now0.setHours(0,0,0,0);
  const end = new Date(now0); end.setDate(now0.getDate()+7);
  const departures = bs.filter(b => { const d=b.travel_start_date?new Date(b.travel_start_date):null; return d && d >= now0 && d <= end; })
    .slice(0,6)
    .map(b => ({
      title: b.package?.title || '—',
      date: new Date(b.travel_start_date).toISOString(),
      customer: b.customer?.name || '—',
      customerId: b.customer?.id ?? b.customerId ?? b.customer_id,
    }));

  return {
    queries: queries,
    recentQueries,
    queriesOverTime,
    queriesByType: queriesByTypeArr,
    queriesByAssignee: queriesByAssigneeArr,
    followups: followups,
    bookings: bs,
    paymentsReceived: received.slice(0,7),
    paymentsPending: pending.slice(0,7),
    bookingsByDate,
    departures,
    stats: {
      customersTotal,
      customersNew30,
      queriesTotal,
      queriesNew30,
      followupsToday: followups.length,
      bookedAmt30,
      paidAmt30,
      markupAmt30,
      telecallingAssignedToday: telecallingTotals.assigned,
      telecallingCompletedToday: telecallingTotals.completed,
      telecallingConvertedToday: telecallingTotals.converted,
    },
  };
}
